/**
 * Learning batches panel (contracts-plan.md 16.7 WP-97b; ledger items 241, 280-283;
 * G8, G9, G11, G13). One entry per learning batch - one version's run of proposed
 * datapoints - listed above PendingPanel's per-datapoint queue.
 *
 * A person decides a batch LINE BY LINE (AM-2): each line has a checkbox, nothing is
 * pre-selected, and the one button, "Accept selected, skip the rest", accepts the
 * checked lines and then rejects whatever is left. There is no accept-all here.
 * The lines are shown in three groups (AM-3): higher than the card's current value,
 * new to the card, and lower or equal - the third behind a "Show lower (n)" control,
 * closed by default. The card's current value per (entry, kind) is handed in by
 * CardView (`values`): its preferred value when one is set, else its latest accepted
 * value. No new fetch: the panel reads the card rows the tab already holds.
 *
 * Every read is defensive (`result?.batches ?? []`): the host suite's mock falls
 * through to `{ data: {} }` for a route it does not know (hazard r).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Check, Layers, Search } from 'lucide-react';
import EmptyState from '../../../ui/EmptyState';
import ConfirmModal from '../../../ui/ConfirmModal';
import { useToast } from '../../../ui/Toast';
import { outlineBtnCls, primaryBtnCls } from '../../../ui/styles';
import { decideRcBatch, getBatchLines, getRcBatches } from '../../api/rateCard';
import type { RateCardEntryRow, RcBatch, RcDatapoint } from '../../api/rateCard';
import { MatchScreen } from './MatchScreen';

/** The card rows' fields the comparison reads (RateCardEntryRow: preferred, latest.value). */
export type CardValueRow = Pick<RateCardEntryRow, 'entry_id' | 'kind' | 'preferred' | 'latest'>;

interface Props {
  /** Called after a decision or a match that could change the card. */
  onChanged: () => void;
  /**
   * The card's rows, from CardView. `undefined` while the card has not loaded: the lines
   * are then held back, never compared against an unknown current value.
   */
  values?: ReadonlyArray<CardValueRow>;
}

/** One request decides at most this many datapoints (ledger 248); the panel repeats. */
const DECIDE_CHUNK = 500;
/** A hard stop on the repeat loop: report what is left rather than looping forever (G13). */
const MAX_REPEAT = 20;
const LINE_PAGE = 500;
const MAX_LINE_PAGES = 20;
const EPSILON = 1e-9;

type GroupKey = 'higher' | 'new' | 'lower';

/** The value the card treats as current for a row: preferred when set, else the latest accepted value. */
export function currentCardValue(row: CardValueRow): number | null {
  return row.preferred ?? row.latest?.value ?? null;
}

function valueKey(entryId: string, kind: string): string {
  return `${entryId}|${kind}`;
}

/**
 * Which group a proposed line belongs to. Compared only like with like (same entry and
 * kind; percentages are fractions on both sides). A line whose number cannot be compared
 * (no numeric value) is kept in the visible "New to the card" group rather than hidden
 * behind the lower group (G9).
 */
function classify(line: RcDatapoint, current: number | null | undefined): GroupKey {
  if (current === null || current === undefined) return 'new';
  if (typeof line.value !== 'number') return 'new';
  return line.value - current > EPSILON ? 'higher' : 'lower';
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

function errorText(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { error?: unknown } } } | null | undefined)?.response?.data;
  if (data && typeof data.error === 'string' && data.error) return data.error;
  const message = (err as { message?: unknown } | null | undefined)?.message;
  return typeof message === 'string' && message ? message : fallback;
}

function dayOf(iso: string | null | undefined): string {
  return typeof iso === 'string' && iso.length >= 10 ? iso.slice(0, 10) : '-';
}

/** Reject every still-proposed row of a batch, repeating while rows remain. */
async function rejectRemaining(batchId: string): Promise<{ decided: number; left: number }> {
  let decided = 0;
  let left = 0;
  for (let i = 0; i < MAX_REPEAT; i += 1) {
    const result = await decideRcBatch(batchId, 'rejected');
    const now = result?.summary?.decided ?? 0;
    decided += now;
    left = result?.summary?.remaining ?? 0;
    if (left <= 0) return { decided, left: 0 };
    if (now === 0) return { decided, left };
  }
  return { decided, left };
}

const GROUP_META: Record<GroupKey, { title: string; hint: string }> = {
  higher: { title: 'Higher than the card', hint: 'Above the value the card holds now' },
  new: { title: 'New to the card', hint: 'The card has no current value for these' },
  lower: { title: 'Lower or equal', hint: 'At or below the value the card holds now' },
};

function LineRow({
  line,
  current,
  checked,
  disabled,
  onToggle,
}: {
  line: RcDatapoint;
  current: number | null | undefined;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  return (
    <li data-testid={`rc-batch-line-${line.id}`}>
      <label className="flex min-h-[44px] cursor-pointer items-center gap-3 px-4 py-3 text-sm transition-colors duration-150 hover:bg-gray-50 sm:min-h-0 dark:hover:bg-gray-700/50">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
          checked={checked}
          disabled={disabled}
          onChange={onToggle}
          aria-label={`Accept ${line.entry_name ?? line.entry_id} (${line.kind})`}
          data-testid={`rc-batch-check-${line.id}`}
        />
        <span className="min-w-0 flex-1 truncate">
          <span className="font-medium text-gray-900 dark:text-gray-100">{line.entry_name ?? line.entry_id}</span>
          <span className="ml-2 text-gray-500 dark:text-gray-400">{line.kind}</span>
        </span>
        <span className="flex-shrink-0 tabular-nums font-medium text-gray-900 dark:text-gray-100">
          {line.value ?? line.value_text ?? '-'}
        </span>
        <span className="hidden w-40 flex-shrink-0 text-xs text-gray-500 dark:text-gray-400 xl:block">
          {current === null || current === undefined ? 'Not on the card yet' : `Card: ${current}`}
        </span>
      </label>
    </li>
  );
}

function BatchRow({
  batch,
  values,
  refreshKey,
  onDecided,
  onMatch,
}: {
  batch: RcBatch;
  values: ReadonlyArray<CardValueRow> | undefined;
  refreshKey: number;
  onDecided: () => void;
  onMatch: (versionId: string) => void;
}) {
  const { toast } = useToast();
  const batchId = batch.batch_id;
  const rowId = batchId ?? 'unbatched';
  const [open, setOpen] = useState(false);
  const [showLower, setShowLower] = useState(false);
  const [lines, setLines] = useState<RcDatapoint[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [confirmSkip, setConfirmSkip] = useState(false);

  // Load every page of the batch's lines when the row is opened, and again after any
  // decision or match (refreshKey), so a stale list is never acted on.
  useEffect(() => {
    if (!open || batchId === null) return undefined;
    let cancelled = false;
    setLines(null);
    setLoadError(null);
    setChecked(new Set());
    (async () => {
      try {
        const all: RcDatapoint[] = [];
        let offset = 0;
        let hasMore = true;
        let pages = 0;
        while (hasMore && pages < MAX_LINE_PAGES) {
          const result = await getBatchLines(batchId, { limit: LINE_PAGE, offset });
          const rows = result?.datapoints ?? [];
          all.push(...rows);
          offset += rows.length;
          pages += 1;
          hasMore = result?.page?.hasMore === true && rows.length > 0;
        }
        if (cancelled) return;
        setLines(all);
        setTruncated(hasMore);
      } catch (err) {
        if (cancelled) return;
        setLines([]);
        setLoadError(errorText(err, 'Could not load the lines of this batch.'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, batchId, refreshKey]);

  const valueMap = useMemo(() => {
    const map = new Map<string, number | null>();
    for (const row of values ?? []) map.set(valueKey(row.entry_id, row.kind), currentCardValue(row));
    return map;
  }, [values]);

  const groups = useMemo(() => {
    const out: Record<GroupKey, RcDatapoint[]> = { higher: [], new: [], lower: [] };
    for (const line of lines ?? []) out[classify(line, valueMap.get(valueKey(line.entry_id, line.kind)))].push(line);
    return out;
  }, [lines, valueMap]);

  function toggle(id: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function acceptSelected() {
    if (batchId === null || checked.size === 0 || busy) return;
    // Display order: higher, new, lower - the order the person saw.
    const ids = [...groups.higher, ...groups.new, ...groups.lower].map((l) => l.id).filter((id) => checked.has(id));
    setBusy(true);
    let touched = false;
    try {
      let accepted = 0;
      let notDecided = 0;
      let remaining: number | undefined;
      for (let i = 0; i < ids.length; i += DECIDE_CHUNK) {
        const result = await decideRcBatch(batchId, 'accepted', ids.slice(i, i + DECIDE_CHUNK));
        touched = true;
        accepted += result?.summary?.decided ?? 0;
        notDecided += result?.summary?.skipped ?? 0;
        remaining = result?.summary?.remaining;
      }
      let skippedRest = 0;
      let left = 0;
      if (remaining !== 0) {
        const rest = await rejectRemaining(batchId);
        skippedRest = rest.decided;
        left = rest.left;
      }
      if (left > 0) {
        toast(
          `Accepted ${plural(accepted, 'line')}, but ${plural(left, 'line')} are still proposed after ${MAX_REPEAT} passes. Use Skip all to finish.`,
          { type: 'error', duration: 8000 },
        );
      } else {
        const extra = notDecided > 0 ? ` ${plural(notDecided, 'selected line')} could not be accepted (already decided or not in this batch).` : '';
        toast(`Accepted ${plural(accepted, 'line')}; skipped the other ${skippedRest}.${extra}`, { type: 'success' });
      }
    } catch (err) {
      toast(errorText(err, 'Could not decide this batch.'), { type: 'error' });
    } finally {
      setBusy(false);
      if (touched) onDecided();
    }
  }

  async function skipAll() {
    if (batchId === null) return;
    setConfirmSkip(false);
    setBusy(true);
    let touched = false;
    try {
      const rest = await rejectRemaining(batchId);
      touched = true;
      if (rest.left > 0) {
        toast(`Skipped ${plural(rest.decided, 'line')}; ${rest.left} are still proposed after ${MAX_REPEAT} passes. Run Skip all again.`, {
          type: 'error',
          duration: 8000,
        });
      } else {
        toast(`Skipped ${plural(rest.decided, 'line')}.`, { type: 'info' });
      }
    } catch (err) {
      toast(errorText(err, 'Could not skip this batch.'), { type: 'error' });
    } finally {
      setBusy(false);
      if (touched) onDecided();
    }
  }

  const isUnbatched = batchId === null;
  const title = isUnbatched
    ? 'Unbatched - proposed before batching'
    : `Version ${(batch.version_id ?? batchId ?? '').slice(0, 8)}`;

  return (
    <div
      className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
      data-testid={isUnbatched ? 'rc-batch-unbatched' : `rc-batch-${rowId}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-gray-200 bg-gray-50 px-4 py-2 dark:border-gray-700 dark:bg-gray-800/50">
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{title}</div>
          <div className="text-xs text-gray-600 dark:text-gray-400">
            {plural(batch.pending, 'line')} pending - {batch.entries} {batch.entries === 1 ? 'entry' : 'entries'} - last proposed {dayOf(batch.last_proposed_at)}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!isUnbatched && (
            <button
              type="button"
              className={outlineBtnCls(open)}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              data-testid={`rc-batch-toggle-${rowId}`}
            >
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              Review lines
            </button>
          )}
          {batch.version_id && (
            <button
              type="button"
              className={outlineBtnCls()}
              onClick={() => onMatch(batch.version_id as string)}
              data-testid={`rc-batch-match-${rowId}`}
            >
              <Search className="h-4 w-4" />
              Match unmatched lines
            </button>
          )}
          {!isUnbatched && (
            <button
              type="button"
              className="inline-flex min-h-[44px] items-center rounded-lg px-2.5 py-1.5 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
              disabled={busy}
              onClick={() => setConfirmSkip(true)}
              data-testid={`rc-batch-skip-${rowId}`}
            >
              Skip all
            </button>
          )}
        </div>
      </div>

      {isUnbatched && (
        <p className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">
          These datapoints were proposed before batches existed. Decide them one by one in the list below.
        </p>
      )}

      {open && !isUnbatched && (
        <div data-testid={`rc-batch-lines-${rowId}`}>
          {(lines === null || values === undefined) && !loadError && (
            <p className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">Loading...</p>
          )}
          {loadError && <p className="px-4 py-3 text-sm text-red-600 dark:text-red-400">{loadError}</p>}
          {lines !== null && values !== undefined && lines.length === 0 && !loadError && (
            <p className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">No proposed lines left in this batch.</p>
          )}
          {lines !== null && values !== undefined && lines.length > 0 && (
            <>
              {truncated && (
                <p className="px-4 pt-3 text-xs text-amber-700 dark:text-amber-400">
                  Showing the first {plural(lines.length, 'line')} only; more are proposed in this batch.
                </p>
              )}
              {(['higher', 'new', 'lower'] as GroupKey[]).map((key) => {
                const group = groups[key];
                if (group.length === 0) return null;
                const isLower = key === 'lower';
                return (
                  <div key={key} data-testid={`rc-batch-group-${rowId}-${key}`}>
                    <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-4 py-2 dark:border-gray-700">
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400" title={GROUP_META[key].hint}>
                        {GROUP_META[key].title} ({group.length})
                      </span>
                      {isLower && (
                        <button
                          type="button"
                          className={outlineBtnCls(showLower)}
                          aria-expanded={showLower}
                          onClick={() => setShowLower((v) => !v)}
                          data-testid={`rc-batch-show-lower-${rowId}`}
                        >
                          {showLower ? `Hide lower (${group.length})` : `Show lower (${group.length})`}
                        </button>
                      )}
                    </div>
                    {(!isLower || showLower) && (
                      <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                        {group.map((line) => (
                          <LineRow
                            key={line.id}
                            line={line}
                            current={valueMap.get(valueKey(line.entry_id, line.kind))}
                            checked={checked.has(line.id)}
                            disabled={busy}
                            onToggle={() => toggle(line.id)}
                          />
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
              <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 px-4 py-3 dark:border-gray-700">
                <span className="text-xs text-gray-600 dark:text-gray-400" data-testid={`rc-batch-selected-${rowId}`}>
                  {checked.size} selected
                </span>
                <button
                  type="button"
                  className={primaryBtnCls}
                  disabled={checked.size === 0 || busy}
                  onClick={acceptSelected}
                  data-testid={`rc-batch-accept-${rowId}`}
                >
                  <Check className="h-4 w-4" />
                  Accept selected, skip the rest
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <ConfirmModal
        isOpen={confirmSkip}
        onClose={() => setConfirmSkip(false)}
        onConfirm={skipAll}
        title="Skip every line in this batch?"
        message={`All ${plural(batch.pending, 'pending line')} in this batch will be rejected and none will enter the card. This cannot be undone from any screen here.`}
        confirmLabel="Skip every line"
        danger
      />
    </div>
  );
}

export function LearningPanel({ onChanged, values }: Props) {
  const [batches, setBatches] = useState<RcBatch[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [matchVersionId, setMatchVersionId] = useState<string | null>(null);

  const reload = useCallback(() => {
    getRcBatches()
      .then((result) => {
        setBatches(result?.batches ?? []);
        setLoadError(null);
      })
      .catch((err) => {
        setBatches((prev) => prev ?? []);
        setLoadError(errorText(err, 'Could not load the learning batches.'));
      });
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  // After any successful decision: refresh this panel, then tell the card.
  const afterDecision = useCallback(() => {
    setRefreshKey((k) => k + 1);
    reload();
    onChanged();
  }, [reload, onChanged]);

  if (batches === null) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>;
  }

  return (
    <div className="space-y-3" data-testid="rc-learning-panel">
      {loadError && <p className="text-sm text-red-600 dark:text-red-400">{loadError}</p>}
      {batches.length === 0 && !loadError && (
        <EmptyState
          icon={Layers}
          title="No learning batches"
          hint="Mark a version executed to propose its datapoints (7.3)."
          variant="card"
        />
      )}
      {batches.map((batch) => (
        <BatchRow
          key={batch.batch_id ?? 'unbatched'}
          batch={batch}
          values={values}
          refreshKey={refreshKey}
          onDecided={afterDecision}
          onMatch={setMatchVersionId}
        />
      ))}
      {matchVersionId !== null && (
        <MatchScreen
          versionId={matchVersionId}
          onClose={() => setMatchVersionId(null)}
          onChanged={afterDecision}
        />
      )}
    </div>
  );
}

export default LearningPanel;
